import { FocusEvent, useCallback, useRef } from "react";

/**
 * Select a field's contents when the reviewer tabs into it, so the next
 * keystroke replaces the value instead of appending to it.
 *
 * This is what makes a poor scan bearable. When extraction cannot read a
 * document the reviewer's job stops being review and becomes transcription, and
 * transcription over pre-filled fields means clearing each one before typing it.
 *
 * Spread onto a container rather than applied per field: the form has around
 * sixty inputs across five sections and two repeated groups, a per-field prop
 * would have to be threaded through every shared control that renders one, and a
 * field added later would silently miss out.
 */

/**
 * Input types whose contents can be selected. `select()` throws on a date,
 * number-stepper or colour input in some engines, and means nothing on a
 * checkbox or a button.
 */
const SELECTABLE_INPUT_TYPES: ReadonlySet<string> = new Set([
  "text",
  "search",
  "url",
  "tel",
  "password",
  "email",
]);

function isSelectable(
  target: EventTarget | null
): target is HTMLInputElement | HTMLTextAreaElement {
  if (target instanceof HTMLTextAreaElement) return true;
  return target instanceof HTMLInputElement && SELECTABLE_INPUT_TYPES.has(target.type);
}

/**
 * Props for the container holding the fields.
 *
 * Only a keyboard focus selects. A reviewer who clicked into the middle of a
 * value is placing a caret — they want to fix a character, not replace the
 * field — and selecting everything there would throw away the click they just
 * made. Tabbing carries no such intent, which is exactly when replacing is what
 * they want.
 */
export function useSelectOnKeyboardFocus(): {
  onPointerDownCapture: () => void;
  onFocusCapture: (event: FocusEvent<HTMLElement>) => void;
} {
  // A pointer press is what disqualifies the focus that follows it. Held in a
  // ref because nothing renders from it, and cleared on the focus it explains
  // rather than on a timer, so a press that never lands on a field cannot leak
  // into the next tab.
  const fromPointer = useRef(false);

  const onPointerDownCapture = useCallback(() => {
    fromPointer.current = true;
  }, []);

  const onFocusCapture = useCallback((event: FocusEvent<HTMLElement>) => {
    const wasPointer = fromPointer.current;
    fromPointer.current = false;
    if (wasPointer) return;
    if (!isSelectable(event.target)) return;
    event.target.select();
  }, []);

  return { onPointerDownCapture, onFocusCapture };
}
